
import { supabase } from "@/integrations/supabase/client";

/**
 * Check if a user account with the provided email already exists
 * @param email The email address to check
 * @returns A promise that resolves to true if the email exists, false otherwise
 */
export async function checkEmailExists(email: string): Promise<boolean> {
  try {
    console.log(`🔍 AUTH: Checking if email exists: ${email}`);
    
    // A more reliable approach is to use the admin functions to check if the email exists
    const { data, error } = await supabase.auth.admin.listUsers({
      // We'll filter for this email only in our code to be more explicit
      page: 1,
      perPage: 1
    });
    
    if (error) {
      console.error("🔍 AUTH ERROR: Error checking if email exists:", error);
      // In case of an error, don't block the signup
      return false;
    }
    
    // If we find a user with this email in the admin list, then it exists
    // Properly type the users array to avoid the TypeScript error
    const emailExists = data && data.users && data.users.some(
      (user: any) => {
        return user && typeof user === 'object' && 'email' in user && 
          user.email && user.email.toLowerCase() === email.toLowerCase();
      }
    );
    
    if (emailExists) {
      console.log(`🔍 AUTH: Email exists: ${email}`);
      return true;
    }
    
    // Otherwise, let's try with the signUp method with a fake password that doesn't meet minimum requirements
    // This won't create an account but will tell us if the email is registered
    const { error: signUpError } = await supabase.auth.signUp({
      email,
      password: 'a' // Intentionally short password that won't meet requirements
    });
    
    // If the error contains "already registered", the email exists
    if (signUpError && 
        signUpError.message &&
        (signUpError.message.includes('already registered') || 
         signUpError.message.includes('already in use'))) {
      console.log(`🔍 AUTH: Email exists (from signUp attempt): ${email}`);
      return true;
    }
    
    // Otherwise, assume the email doesn't exist
    console.log(`🔍 AUTH: Email does not exist: ${email}`);
    return false;
  } catch (error) {
    console.error("🔍 AUTH ERROR: Error checking if email exists:", error);
    // In case of any error, we assume the email doesn't exist to avoid blocking signups
    return false;
  }
}
